/**
 * Месяц графика как сетка календаря: что за день в каждой клетке и чем это задано.
 *
 * Сводит в одно две вещи, которые в кабинете живут порознь: недельный шаблон отдела
 * (маска рабочих дней, обычное время, своё время отдельных дней) и записи календаря
 * праздников (праздник, выходной, рабочий день по переносу). Порядок тот же, что у
 * отчёта: запись календаря побеждает шаблон, а на одну дату запись отдела побеждает
 * запись компании и вышестоящих отделов.
 *
 * Чистые функции на строках `YYYY-MM-DD` — без часовых поясов (см. `dates.ts`).
 */
import type {CalendarException, CalendarKind} from './types';

export type WeekTemplate = {
  /** Бит 0 — понедельник. */
  mask: number;
  start: number | null;
  end: number | null;
  dayTimes: ReadonlyArray<{weekday: number; startMinutes: number; endMinutes: number}>;
};

/**
 * - `work` / `off` — по шаблону;
 * - `holiday` / `day_off` / `workday` — по записи календаря;
 * - `plain` — шаблона нет (календарь компании): день ничем не задан.
 */
export type DayKind = 'work' | 'off' | 'plain' | CalendarKind;

export type MonthCell = {
  date: string;
  day: number;
  /** 0 — пн … 6 — вс. */
  weekday: number;
  /** Клетка из соседнего месяца, добивающая неделю. */
  inMonth: boolean;
  kind: DayKind;
  start: number | null;
  end: number | null;
  /** Запись календаря, которая задала день, если задала. */
  exception: CalendarException | null;
  /** Запись пришла сверху (компания или вышестоящий отдел), а не своя. */
  inherited: boolean;
};

const pad = (n: number) => String(n).padStart(2, '0');

function isoOf(t: number): string {
  const d = new Date(t);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/** Первый и последний день месяца, `month` — 1..12. */
export function monthRange(year: number, month: number): {from: string; to: string} {
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return {from: `${year}-${pad(month)}-01`, to: `${year}-${pad(month)}-${pad(last)}`};
}

/** Соседний месяц: `step` = -1 / +1. */
export function shiftMonth(year: number, month: number, step: number): {year: number; month: number} {
  const i = year * 12 + (month - 1) + step;
  return {year: Math.floor(i / 12), month: (i % 12) + 1};
}

/** Какая запись действует на дату: своя побеждает пришедшую сверху. */
function exceptionFor(
  date: string,
  list: ReadonlyArray<CalendarException>,
  own: string,
): CalendarException | null {
  const onDate = list.filter(x => x.date === date);
  return onDate.find(x => x.department_id === own) ?? onDate[0] ?? null;
}

/**
 * Сетка месяца с понедельника: целые недели, дни соседних месяцев помечены
 * `inMonth: false`. `own` — id отдела, чьи записи свои ('' — компания).
 */
export function monthGrid(
  year: number,
  month: number,
  template: WeekTemplate | null,
  exceptions: ReadonlyArray<CalendarException>,
  own: string,
): MonthCell[] {
  const first = Date.UTC(year, month - 1, 1);
  const lead = (new Date(first).getUTCDay() + 6) % 7;
  const days = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const total = Math.ceil((lead + days) / 7) * 7;
  const cells: MonthCell[] = [];
  for (let i = 0; i < total; i += 1) {
    const t = first + (i - lead) * 86_400_000;
    const d = new Date(t);
    const date = isoOf(t);
    const weekday = (d.getUTCDay() + 6) % 7;
    const x = exceptionFor(date, exceptions, own);
    const timed = template?.start != null && template?.end != null;
    let kind: DayKind;
    let start: number | null = null;
    let end: number | null = null;
    if (x) {
      kind = x.kind;
      // Перенос берёт ОБЩЕЕ время шаблона, не время этого дня недели.
      if (x.kind === 'workday' && timed) {
        start = template!.start;
        end = template!.end;
      }
    } else if (!template) {
      kind = 'plain';
    } else if ((template.mask & (1 << weekday)) !== 0) {
      kind = 'work';
      if (timed) {
        const own = template.dayTimes.find(dt => dt.weekday === weekday);
        start = own ? own.startMinutes : template.start;
        end = own ? own.endMinutes : template.end;
      }
    } else {
      kind = 'off';
    }
    cells.push({
      date,
      day: d.getUTCDate(),
      weekday,
      inMonth: d.getUTCMonth() === month - 1,
      kind,
      start,
      end,
      exception: x,
      inherited: !!x && x.department_id !== own,
    });
  }
  return cells;
}

/** Длина смены в минутах; ночная (конец раньше начала) переходит через полночь. */
export function shiftMinutes(start: number | null, end: number | null): number {
  if (start == null || end == null) {
    return 0;
  }
  return end > start ? end - start : end + 1440 - start;
}

export type MonthTotals = {
  workDays: number;
  /** Норма часов месяца в минутах — по шаблону и переносам, без перерывов. */
  minutes: number;
  holidays: number;
  daysOff: number;
};

/** Итоги месяца — только клетки самого месяца. */
export function monthTotals(cells: ReadonlyArray<MonthCell>): MonthTotals {
  const inMonth = cells.filter(c => c.inMonth);
  const working = inMonth.filter(c => c.kind === 'work' || c.kind === 'workday');
  return {
    workDays: working.length,
    minutes: working.reduce((sum, c) => sum + shiftMinutes(c.start, c.end), 0),
    holidays: inMonth.filter(c => c.kind === 'holiday').length,
    daysOff: inMonth.filter(c => c.kind === 'off' || c.kind === 'day_off').length,
  };
}

// ---------------------------------------------------------------------------
// Выделение нескольких дней в сетке.
// ---------------------------------------------------------------------------

/** Даты от `a` до `b` включительно в порядке сетки — для Shift+клик. */
export function rangeBetween(
  cells: ReadonlyArray<MonthCell>,
  a: string,
  b: string,
): string[] {
  const i = cells.findIndex(c => c.date === a);
  const j = cells.findIndex(c => c.date === b);
  if (i < 0 || j < 0) {
    return [b];
  }
  const [from, to] = i <= j ? [i, j] : [j, i];
  return cells.slice(from, to + 1).map(c => c.date);
}

/** Все дни месяца с этим днём недели — нажатие на заголовок столбца. */
export function weekdayDates(cells: ReadonlyArray<MonthCell>, weekday: number): string[] {
  return cells.filter(c => c.inMonth && c.weekday === weekday).map(c => c.date);
}

/**
 * Добавить даты к выделению — или снять их, если все они уже выделены. Так одно и то
 * же нажатие на «Сб» и выделяет субботы, и снимает их.
 */
export function toggleDates(selected: ReadonlySet<string>, dates: ReadonlyArray<string>): Set<string> {
  const next = new Set(selected);
  const all = dates.length > 0 && dates.every(d => next.has(d));
  for (const d of dates) {
    if (all) {
      next.delete(d);
    } else {
      next.add(d);
    }
  }
  return next;
}
